import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense, useRef } from 'react';
import { Layout, Menu, Button, theme, Avatar, Dropdown, Space, Breadcrumb, Divider, Typography, Select, Tag, Checkbox, Modal, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ScheduleOutlined,
  CarOutlined,
  SolutionOutlined,
  SettingOutlined,
  LogoutOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  FolderOpenOutlined,
  CloseOutlined,
  MenuOutlined,
  BankOutlined,
  CalendarOutlined,
  MoreOutlined,
  SwapOutlined,
  HomeOutlined,
  ContactsOutlined,
  CreditCardOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { RiGuideFill } from "react-icons/ri";
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../actions/authActions';
import logo from '../../public/assets/logo/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionTimer from './sessionTimer';
import ConfirmModal from './confirmModal';
import NotFound from './notFound';
import { userActions } from '../actions/userActions';
import SubscriptionBanner from './subscriptionBanner';
import { subscriptionActions } from '../actions/subscriptionActions';
import { tenantActions } from '../actions/tenantActions';
import { branchActions } from '../actions/branchActions';
import { getPageAccess, getblobLink } from '../services/helper';
import { registerSessionInvalidCallback, validateSession } from '../services/sessionGuard';

const ChatbotAssistant = lazy(() => import('./chatbot/assistantChatbot'));
const { Header, Sider, Content } = Layout;
import { RENEW_ROUTE } from '../utils/subscriptionGuard';

interface MainLayoutProps {
  children: React.ReactNode;
}

const SUBSCRIPTION_MODAL_DISMISS_KEY = 'dsms_subscription_modal_dismissed';
const makeSubscriptionDismissSignature = (expired: boolean, endDate?: string | null) =>
  `${expired ? 'expired' : 'expiring'}|${endDate || ''}`;

const MENU_GROUPS: { key: string; label: string; routes: string[] }[] = [
  { key: 'grp-overview', label: 'Overview', routes: ['/dashboard'] },
  { key: 'grp-operations', label: 'Operations', routes: ['/users', '/enrollments', '/batches', '/vehicles'] },
  { key: 'grp-catalog', label: 'Catalog', routes: ['/services', '/categories'] },
  { key: 'grp-finance', label: 'Finance', routes: ['/accounting', '/subscription-plans'] },
  { key: 'grp-crm', label: 'CRM', routes: ['/leads', '/queries', '/userGuide'] },
  { key: 'grp-admin', label: 'Administration', routes: ['/tenants', '/tenants/view', '/settings'] },
];

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {

  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [subscriptionModalData, setSubscriptionModalData] = useState<{ expired: boolean; endDate?: string | null }>({ expired: false, endDate: null });
  const [dontShowSubscriptionModal, setDontShowSubscriptionModal] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState<boolean>(
    () => localStorage.getItem('dsms_chatbot_enabled') !== 'false'
  );
  const location = useLocation();
  const auth = JSON.parse(localStorage.getItem("AUTHDATA") || '{}');
  const role = auth?.role;
  const appState = useSelector((state: any) => state);
  const pageAccess = getPageAccess('/settings', appState);

  const profile = useSelector((state: any) => state.getProfileReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authPresent = Boolean(localStorage.getItem('AUTHDATA'));
  const shouldHideMenu = authPresent && (!profile || (profile.isLoading === true) && !profile.profileData);
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
  const subscriptionStatus = useSelector((state: any) => state.getSubscriptionSyncStatusReducer);
  const userSubscriptionstatus = useSelector((state: any) => state.userSubscriptionStatusReducer);

  const headerTenantList = useSelector((state: any) => state.getHeaderTenantListReducer);
  const switchTenantState = useSelector((state: any) => state.switchTenantReducer);
  const fileUpload = useSelector((state: any) => state.fileUploadReducer);

  const authData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('AUTHDATA') || '{}'); } catch { return {}; }
  }, []);
  const isSuperAdmin = authData?.role === 'SUPER_ADMIN';
  const isAdmin = authData?.role === 'ADMIN';
  const [activeTenantId, setActiveTenantId] = useState<string>(
    String(authData?.lastLoginTenantId || authData?.tenantId || '')
  );

  const liveAvailableTenants = profile?.profileData?.availableTenants || authData?.availableTenants || [];
  const liveTenantName = profile?.profileData?.tenantName || authData?.tenantName || '';

  const getAvatarUrlFromData = useCallback((profileData: any): string => {
    if (!profileData) return '';
    if (profileData.profileImageUrl) return profileData.profileImageUrl;
    const img = profileData.profileImage;
    if (typeof img === 'string') {
      return img.startsWith('http') || img.startsWith('data:') ? img : `${getblobLink()}/${img}`;
    }
    if (img?.url) return img.url;
    if (img?.blobUrl) return `${getblobLink()}/${img.blobUrl}`;
    if (img?.buffer) return `data:${img.mimetype || 'image/png'};base64,${img.buffer}`;
    return '';
  }, []);

  const handleLogout = () => {
    let sessionId: string | undefined;
    try {
      const parsed = JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
      sessionId = parsed?.sessionId;
    } catch (_) { }
    dispatch(authActions.logoutRequest({ sessionId }));

    localStorage.removeItem('AUTHDATA');
    sessionStorage.removeItem('TS');
    localStorage.removeItem('SESSIONID');
    navigate('/', { replace: true });
  };

useEffect(() => {
  const handleAuthStateChanged = () => {
    handleLogout();
  };

  window.addEventListener('authState', handleAuthStateChanged);

  return () => {
    window.removeEventListener('authState', handleAuthStateChanged);
  };
}, []);

  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    if (data) {
      try {
        const profileData = JSON.parse(data);
        const userId = profileData?.id || profileData?.userId || profileData?._id;
        if (userId) {
          dispatch(userActions.getProfileRequest({ id: userId }));
        }
      } catch (_) {}
    }
  }, [dispatch]);

  useEffect(() => {
    if (profile?.profileData) {
      const url = getAvatarUrlFromData(profile.profileData);
      if (url) setAvatarUrl(url);
    }
  }, [profile?.profileData, getAvatarUrlFromData]);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    const profileData = JSON.parse(String(data));
    if (data && profileData?.tenantId) {
      dispatch(subscriptionActions.getUserSubscriptionStatusRequest());
    }
  }, []);

 
  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    if (data) {
      const profileData = JSON.parse(String(data));
      dispatch(userActions.getProfileRequest({ id: profileData?.id }));
    }
  }, [fileUpload?.isSuccess]);

  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    const profileData = JSON.parse(String(data));
    if (userSubscriptionstatus?.isSuccess && userSubscriptionstatus?.data) {
      const { endDate, isExpired } = userSubscriptionstatus.data;
      const isEndDateCrossed = isExpired === true || (endDate && new Date(endDate).getTime() <= Date.now());
      const currentRole = profileData?.role;
      const isAdminRole = currentRole === 'ADMIN';
      const isStaffOrInstructor = currentRole === 'STAFF' || currentRole === 'INSTRUCTOR';
      const shouldSync = (isAdminRole && isEndDateCrossed) || (isStaffOrInstructor && isExpired === true);
      if (shouldSync) {
        dispatch(subscriptionActions.getSubscriptionSyncStatusRequest({ tenantId: profileData?.tenantId }));
      }
    }
  }, [userSubscriptionstatus]);

  useEffect(() => {
    const syncChatbot = () => {
      setChatbotEnabled(localStorage.getItem('dsms_chatbot_enabled') !== 'false');
    };
    window.addEventListener('dsms_chatbot_pref_changed', syncChatbot);
    return () => window.removeEventListener('dsms_chatbot_pref_changed', syncChatbot);
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(tenantActions.getHeaderTenantList({ pageNumber: 1, pageSize: 0, isActive: true }));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin && authData?.tenantId) {
      dispatch(tenantActions.getAccessControl(String(authData.tenantId)));
    }
  }, [isSuperAdmin, authData?.tenantId]);

  useEffect(() => {
    if (switchTenantState?.isSuccess && switchTenantState?.data) {
      const data = switchTenantState.data;

      const newAuthToken = data?.authToken;
      if (!newAuthToken) {
        console.error('[SwitchTenant] No authToken in API response');
        return;
      }

      // Update localStorage AUTHDATA with new tenant info
      let currentAuth: Record<string, any> = {};
      try { currentAuth = JSON.parse(localStorage.getItem('AUTHDATA') || '{}'); } catch { /* ignore */ }

      const updatedAuth = {
        ...currentAuth,
        tenantId: data.tenantId,
        tenantName: data.tenantName || currentAuth.tenantName,
        lastLoginTenantId: data.tenantId,
        isBranchEnabled: data.isBranchEnabled ?? false,
        role: data.role || currentAuth.role,
        sessionId: data.sessionId,
        authToken: newAuthToken,
        tokenExpiry: data.tokenExpiry || currentAuth.tokenExpiry,
      };
      localStorage.setItem('AUTHDATA', JSON.stringify(updatedAuth));
      sessionStorage.setItem('TS', JSON.stringify({
        authToken: newAuthToken,
        tenantId: data.tenantId,
      }));

      window.location.href = '/dashboard';
    }
  }, [switchTenantState]);

  const handleTenantSwitch = useCallback((tenantId: string) => {
    if (tenantId && tenantId !== activeTenantId) {
      dispatch(branchActions.switchTenant({ tenantId }));
    }
  }, [activeTenantId, dispatch]);

  const allMenuItems = useMemo(() => ([
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/users', icon: <TeamOutlined />, label: 'Users' },
    { key: '/enrollments', icon: <IdcardOutlined />, label: 'Enrollment' },
    { key: '/services', icon: <SolutionOutlined />, label: 'Services' },
    { key: '/categories', icon: <FolderOpenOutlined />, label: 'Category' },
    { key: '/batches', icon: <ScheduleOutlined />, label: 'Batches' },
    { key: '/vehicles', icon: <CarOutlined />, label: 'Vehicles' },
    { key: '/accounting', icon: <DollarOutlined />, label: 'Accounting' },
    { key: '/leads', icon: <ContactsOutlined />, label: 'Leads' },
    { key: '/queries', icon: <QuestionCircleOutlined />, label: 'Queries' },
    { key: '/userGuide', icon: <RiGuideFill size={'20'} />, label: 'User Guide' },
    { key: '/tenants', icon: <BankOutlined />, label: 'Tenants' },
    { key: '/subscription-plans', icon: <CreditCardOutlined />, label: 'Subscription' },
    { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },

  ]), []);

  const breadcrumbItems = useMemo(() => {
    const path = location.pathname || '/dashboard';
    const parts = path.split('/').filter(Boolean);
    const items: { key: string; label: string; icon?: React.ReactNode }[] = [];
    if (parts.length > 0) {
      const firstPath = `/${parts[0]}`;
      const match = allMenuItems.find(m => m.key === firstPath);
      if (match) {
        items.push({ key: match.key, label: match.label, icon: match.icon });
      } else {
        items.push({ key: firstPath, label: parts[0].charAt(0).toUpperCase() + parts[0].slice(1) });
      }
    }
    if (parts.length > 1) {
      const second = parts[1];
      const actionLabel = second.match(/^[0-9a-fA-F\-]{6,}$/) ? 'View' : (second.charAt(0).toUpperCase() + second.slice(1));
      items.push({ key: `${parts[0]}/${parts[1]}`, label: actionLabel });
    }
    return items;
  }, [location.pathname, allMenuItems]);

  const accessControlState = useSelector((state: any) => state.getAccessControlReducer);
  const allowedRoutes = useMemo(() => {
    if (isSuperAdmin) return null;
    const accessPoints = accessControlState?.data?.accessPoints;
    if (Array.isArray(accessPoints) && accessPoints.length > 0) {
      return accessPoints
        .filter((ap: any) => ap.isAllowed !== false)
        .map((ap: any) => {
          const r = ap.route || '';
          return r.startsWith('/') ? r : `/${r}`;
        })
        .filter(Boolean);
    }
    const subscription = profile?.profileData?.currentSubscription;
    const plan = subscription?.plan || subscription?.plan_id;
    const planRoutesRaw = plan?.route || plan?.routes;
    if (!Array.isArray(planRoutesRaw) || planRoutesRaw.length === 0) return null;
    const normalized = planRoutesRaw.map((r: any) => {
      const route = typeof r === 'string' ? r : (r?.route || r?.path || '');
      if (!route) return '';
      return route.startsWith('/') ? route : `/${route}`;
    }).filter(Boolean);
    return Array.from(new Set(normalized));
  }, [isSuperAdmin, accessControlState?.data, profile]);

  const menuItems = useMemo(() => {
    if (role === 'SUPER_ADMIN') return [...allMenuItems];
    let items = [...allMenuItems];
    if (Array.isArray(allowedRoutes) && allowedRoutes.length > 0) {
      items = items.filter((item) =>
        allowedRoutes.some((ar) => item.key === ar || item.key.startsWith(ar))
      );
    }
    items = items.filter((item) => item.key !== '/tenants' && item.key !== '/subscription-plans');
    const hasUserGuide = items.some(i => i.key === '/userGuide');
    if (!hasUserGuide) {
      const userGuideItem = allMenuItems.find(i => i.key === '/userGuide');
      if (userGuideItem) items.push(userGuideItem);
    }
    if (role === 'ADMIN' && auth?.tenantId) {
      const hasTenantMenu = items.some(i => i.key?.startsWith('/tenants'));
      if (!hasTenantMenu) {
        const settingsIndex = items.findIndex(item => item.key === '/settings');
        if (settingsIndex !== -1) {
          items.splice(settingsIndex, 0, { key: '/tenants/view', label: 'Organization', icon: <HomeOutlined /> });
        }
      } else {
        items = items.map((item) =>
          item.key?.startsWith('/tenants') ? { ...item, key: `/tenants/view`, label: 'Organization' } : item
        );
      }
    }
    if (role === 'STAFF') {
      items = items.filter((item) => !['/settings', '/batches', '/queries'].includes(item.key));
      const isLeadsAllowed = !allowedRoutes || allowedRoutes.some((ar: string) => ar === '/leads' || ar.startsWith('/leads'));
      const hasLeads = items.some(i => i.key === '/leads');

      if (!hasLeads && isLeadsAllowed) {
        items.push({ key: '/leads', icon: <ContactsOutlined />, label: 'Leads' });
      }
    }
    return items;
  }, [allMenuItems, profile, allowedRoutes, role]);

  const groupedMenuItems = useMemo(() => {
    const groupedRoutes = new Set(MENU_GROUPS.flatMap((g) => g.routes));
    const sections: any[] = MENU_GROUPS
      .map((g) => {
        const children = menuItems.filter((it: any) => g.routes.includes(it.key));
        return children.length ? { key: g.key, type: 'group' as const, label: g.label, children } : null;
      }).filter(Boolean) as any[];
    const leftovers = menuItems.filter((it: any) => !groupedRoutes.has(it.key));
    if (leftovers.length) {
      sections.push({ key: 'grp-more', type: 'group' as const, label: 'More', children: leftovers });
    }
    return sections;
  }, [menuItems]);

  const isRouteAllowed = useMemo(() => {
    if (isSuperAdmin || isAdmin) return true;
    if (!Array.isArray(allowedRoutes) || allowedRoutes.length === 0) return true;
    const current = location.pathname || '/';
    return allowedRoutes.some((ar) => current === ar || current.startsWith(ar + '/') || current.startsWith(ar));
  }, [allowedRoutes, location.pathname, isSuperAdmin, isAdmin]);

  const userMenuItems = [
    { key: 'profile', label: 'My Profile', icon: <UserOutlined /> },
    { key: 'logout', label: 'Logout', icon: <LogoutOutlined /> },
  ];

  const onUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') navigate('/profile');
    if (key === 'logout') handleLogout();
  };

  const handleRenewPlan = () => {
    setSubscriptionModalVisible(false);
    navigate(RENEW_ROUTE);
  };

  const persistSubscriptionDismiss = () => {
    if (dontShowSubscriptionModal) {
      try {
        const signature = makeSubscriptionDismissSignature(subscriptionModalData.expired, subscriptionModalData.endDate);
        localStorage.setItem(SUBSCRIPTION_MODAL_DISMISS_KEY, signature);
      } catch (_) { }
    }
  };

  const handleViewPlans = () => {
    persistSubscriptionDismiss();
    setSubscriptionModalVisible(false);
    navigate('/pricing');
  };

  const handleCloseSubscriptionModal = () => {
    persistSubscriptionDismiss();
    setSubscriptionModalVisible(false);
  };

  useEffect(() => {
    if (profile && profile?.profileData) {
      const profileData = profile.profileData;
      if (profileData?.profileImageUrl) {
        setAvatarUrl(profileData.profileImageUrl);
      } else if (profileData?.profileImage?.blobUrl) {
        const baseUrl = getblobLink();
        setAvatarUrl(`${baseUrl}/${profileData.profileImage.blobUrl}`);
      } else if (profileData?.profileImage?.buffer) {
        const formattedUrl = `data:${profileData.profileImage.mimetype || 'image/png'};base64,${profileData.profileImage.buffer}`;
        setAvatarUrl(formattedUrl);
      } else {
        setAvatarUrl('');
      }
    }
  }, [profile]);

  useEffect(() => {
    try {
      if (!profile) return;
      const failed = profile.isError === true;
      const emptyProfile = profile.isSuccess === true && !profile.profileData;
      if (failed || emptyProfile) {
        const authData = localStorage.getItem('AUTHDATA');
        if (!authData) return;
        const parsed = JSON.parse(authData);
        if (!parsed?.authToken) return;
        dispatch(authActions.logoutRequest());
        navigate('/login', { replace: true });
      }
    } catch (_) { }
  }, [profile, dispatch, navigate]);

  useEffect(() => {
    try {
      const sub = profile?.profileData?.currentSubscription;
      if (!sub) return;
      const endDateStr = sub.endDate || sub.expiryDate || null;
      if (!endDateStr) return;
      const end = new Date(endDateStr);
      if (Number.isNaN(end.getTime())) return;
      const now = new Date();
      const msInDay = 1000 * 60 * 60 * 24;
      const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msInDay);
      const expired = end.getTime() <= now.getTime();
      if (!expired && daysLeft > 7) return;
      const signature = makeSubscriptionDismissSignature(expired, end.toISOString());
      if (localStorage.getItem(SUBSCRIPTION_MODAL_DISMISS_KEY) === signature) return;
      setSubscriptionModalData({ expired, endDate: end.toISOString() });
      setDontShowSubscriptionModal(false);
      setSubscriptionModalVisible(true);
    } catch (err) { }
  }, [profile]);

  useEffect(() => {
    const event = new CustomEvent('sidebar-toggle', { detail: { collapsed } });
    window.dispatchEvent(event);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
  }, [collapsed]);

  const [sessionExpiredVisible, setSessionExpiredVisible] = useState(false);
  const isValidatingSession = useRef(false);

  useEffect(() => {
    registerSessionInvalidCallback(() => setSessionExpiredVisible(true));
  }, []);

  const handleSessionExpiredConfirm = useCallback(() => {
    dispatch(authActions.logoutRequest());
    setSessionExpiredVisible(false);
    localStorage.removeItem('AUTHDATA');
    sessionStorage.removeItem('TS');
    window.dispatchEvent(new Event('authStateChanged'));
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  useEffect(() => {
    const authData = localStorage.getItem('AUTHDATA');
    if (!authData) return;
    const publicPaths = ['/login', '/register', '/verify-registration', '/pricing'];
    if (publicPaths.some(p => location.pathname.startsWith(p))) return;

    if (isValidatingSession.current) return;
    isValidatingSession.current = true;

    validateSession()
      .then(valid => {
        if (!valid) setSessionExpiredVisible(true);
      })
      .catch(() => { })
      .finally(() => { isValidatingSession.current = false; });
  }, [location.pathname]);


  // Shared sidebar menu content (used in both desktop Sider and mobile Drawer)
  const handleMenuClick = (key: string) => {
    if (key === '/tenants/view' && role === 'ADMIN') {
      navigate('/tenants/view', { state: { tenantId: auth?.tenantId } });
    } else {
      navigate(key);
    }
    setMobileDrawerOpen(false);
  };

  const SidebarContent = () => (
    <div className="sider-inner">
      <div className="sidebar-logo">
        <div className="sidebar-logo-left">
          {!collapsed ? (
            <div className="sidebar-logo-badge" onClick={() => navigate('/')}>
              <img src={`${getblobLink()}/public/asset/logo_with_name.png`} alt="DriveMynt Logo"
          loading="lazy" width="180" height="180"/>
            </div>
          ) : (
            <div className="sidebar-logo-collapsed">
              <MenuOutlined onClick={() => setCollapsed(false)} className="menu-toggle-icon" />
            </div>
          )}
        </div>
        <div className="sidebar-logo-right">
          <Button
            type="text"
            className={`header-toggle-btn ${collapsed ? 'hidden' : ''}`}
            icon={<CloseOutlined className="menu-close-icon" />}
            onClick={() => setCollapsed(!collapsed)}
            aria-label="Toggle sidebar"
          />
        </div>
      </div>
      <div className="sidebar-divider">
        <Divider className="sidebar-divider-line" />
      </div>
      <div className="menu-scroll">
        {shouldHideMenu ? (
          <div className="sidebar-menu-placeholder" />
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={collapsed ? menuItems : groupedMenuItems}
            onClick={({ key }) => handleMenuClick(key)}
          />
        )}
      </div>
      <div className="sidebar-profile">
        <Dropdown menu={{ items: userMenuItems, onClick: onUserMenuClick }} placement="topLeft">
          <div
            className={collapsed ? "sidebar-profile-inner collapsed-profile" : "sidebar-profile-inner"}
            role="button"
            tabIndex={0}
            onKeyDown={() => { }}
          >
            <Avatar size={40} src={avatarUrl || undefined} icon={<UserOutlined />} />
            {!collapsed && (
              <div className="sidebar-profile-meta">
                <div className="sidebar-profile-name">
                  {((profile?.profileData?.firstName || '') + ' ' + (profile?.profileData?.lastName || '')).trim() || ''}
                </div>
                <div className="sidebar-profile-email">
                  {profile?.profileData?.role?.roleName || ''}
                </div>
              </div>
            )}
            {!collapsed && <MoreOutlined className="sidebar-profile-menu-icon" />}
          </div>
        </Dropdown>
      </div>
    </div>
  );

  // Mobile drawer sidebar content (always expanded)
  const MobileSidebarContent = () => (
    <div className="sider-inner">
      <div className="sidebar-logo">
        <div className="sidebar-logo-left">
          <div className="sidebar-logo-badge" onClick={() => { navigate('/'); setMobileDrawerOpen(false); }}>
            <Avatar className="sidebar-logo-avatar" size={50} src={logo} />
            <div className="sidebar-logo-text">
              <div className="sidebar-logo-main">DriveMynt</div>
            </div>
          </div>
        </div>
        <div className="sidebar-logo-right">
          <Button
            type="text"
            className="header-toggle-btn"
            icon={<CloseOutlined className="menu-close-icon" />}
            onClick={() => setMobileDrawerOpen(false)}
            aria-label="Close sidebar"
          />
        </div>
      </div>
      <div className="sidebar-divider">
        <Divider className="sidebar-divider-line" />
      </div>
      <div className="menu-scroll">
        {shouldHideMenu ? (
          <div className="sidebar-menu-placeholder" />
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={groupedMenuItems}
            onClick={({ key }) => handleMenuClick(key)}
          />
        )}
      </div>
      <div className="sidebar-profile">
        <Dropdown menu={{ items: userMenuItems, onClick: onUserMenuClick }} placement="topLeft">
          <div className="sidebar-profile-inner" role="button" tabIndex={0} onKeyDown={() => { }}>
            <Avatar size={40} src={avatarUrl || undefined} icon={<UserOutlined />} />
            <div className="sidebar-profile-meta">
              <div className="sidebar-profile-name">
                {((profile?.profileData?.firstName || '') + ' ' + (profile?.profileData?.lastName || '')).trim() || ''}
              </div>
              <div className="sidebar-profile-email">
                {profile?.profileData?.role?.roleName || ''}
              </div>
            </div>
            <MoreOutlined className="sidebar-profile-menu-icon" />
          </div>
        </Dropdown>
      </div>
    </div>
  );

  return (
    <Layout className="driveMynt-layout">
      {/* Desktop/Tablet Sidebar */}
      <Sider
        width={230}
        trigger={null}
        collapsed={collapsed}
        theme="dark"
        className="app-sider app-sider--desktop"
      >
        <SidebarContent />
      </Sider>

      {/* Mobile Drawer Sidebar — reuses same SidebarContent and sider-inner styling */}
      <Drawer
        placement="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        className="app-sider--mobile-drawer"
        width={230}
        closable={false}
        styles={{
          body: { padding: 0, background: '#f3f3f3', overflow: 'hidden' },
          wrapper: { boxShadow: 'none' },
        }}
      >
        <MobileSidebarContent />
      </Drawer>

      <Layout className={collapsed ? 'layout-collapsed' : 'layout-expanded'}>
        <Header className="driveMynt-header">
          {/* Mobile hamburger */}
          <Button
            type="text"
            className="mobile-hamburger-btn"
            icon={<MenuOutlined />}
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Open menu"
          />

          <div className="breadcrumb-wrapper">
            <Breadcrumb>
              {breadcrumbItems.map((it) => (
                <Breadcrumb.Item key={it.key}>
                  <span className="breadcrumb-item-content">
                    {it.icon ? <span className="breadcrumb-icon">{it.icon}</span> : null}
                    <span>{it.label}</span>
                  </span>
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </div>

          <Space size="middle" align="center" className="header-controls">
            {!isSuperAdmin && liveAvailableTenants.length > 1 && (
              <Select
                value={activeTenantId || undefined}
                onChange={handleTenantSwitch}
                placeholder="Switch Tenant"
                loading={switchTenantState?.isLoading}
                disabled={switchTenantState?.isLoading}
                className="header-tenant-select"
                suffixIcon={<SwapOutlined />}
                optionLabelProp="label"
                virtual={false}
                listHeight={256}
                options={liveAvailableTenants.map((t: any) => ({ label: t.name, value: String(t._id) }))
                }
                showSearch
                filterOption={(input: string, option: any) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
            {!isSuperAdmin && liveAvailableTenants.length <= 1 && liveTenantName && (
              <Tag color="blue" className="header-tenant-tag">
                <BankOutlined style={{ marginRight: 6 }} />
                {liveTenantName}
              </Tag>
            )}
            {isSuperAdmin && (() => {
              const allTenants = headerTenantList?.tenantData?.tenants || [];
              const selectableTenants = allTenants.filter((t: any) => t.isMainTenant);
              const activeTenant = allTenants.find((t: any) => String(t._id) === activeTenantId);
              const selectValue = activeTenant
                ? { value: activeTenantId, label: activeTenant.name }
                : undefined;
              return (
                <Select
                  labelInValue
                  value={selectValue}
                  onChange={(opt: any) => handleTenantSwitch(opt?.value)}
                  placeholder={headerTenantList?.isLoading ? 'Loading tenants...' : 'Switch Tenant'}
                  loading={headerTenantList?.isLoading || switchTenantState?.isLoading}
                  disabled={switchTenantState?.isLoading}
                  className="header-tenant-select header-tenant-switcher"
                  suffixIcon={<SwapOutlined />}
                  virtual={false}
                  options={selectableTenants.map((t: any) => ({
                    label: t.name,
                    value: String(t._id),
                  }))}
                  showSearch
                  filterOption={(input: string, option: any) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              );
            })()}
          </Space>
        </Header>

        {role !== 'SUPER_ADMIN' && <SubscriptionBanner />}
        <Content
          className="layout-content"
          style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
        >
          <SessionTimer />
          {isRouteAllowed ? children : <NotFound />}
        </Content>
      </Layout>

      {role !== 'SUPER_ADMIN' &&
        <ConfirmModal visible={subscriptionModalVisible} title={null} onOk={handleRenewPlan} onCancel={handleCloseSubscriptionModal}
          footer={null} className="subscription-modal" width={420} bodyStyle={{ padding: 15, textAlign: 'center' }}
          content={(
            <>
              <div className="modal-icon-wrapper">
                <div className="modal-icon-circle">
                  <CalendarOutlined style={{ fontSize: 28, color: '#d94660' }} />
                </div>
              </div>

              <Typography.Title level={4} className="modal-title">
                {subscriptionModalData.expired ? 'Your Plan Has Expired' : 'Free Trial Ending Soon'}
              </Typography.Title>

              <Typography.Paragraph className="modal-desc">
                {subscriptionModalData.expired
                  ? 'Your Free Plan has expired. Please upgrade to a Premium Plan to continue using DriveMynt services without interruption.'
                  : `Your Free Plan will expire on ${subscriptionModalData.endDate
                    ? new Date(subscriptionModalData.endDate).toLocaleDateString()
                    : 'the upcoming date'
                  }. Upgrade to a Premium Plan to continue using DriveMynt services without interruption.`}

              </Typography.Paragraph>
              <div className="modal-actions">
                <Button block onClick={handleViewPlans} className="btn-primary hover-effect">
                  View Plans
                </Button>
                <Button block onClick={handleCloseSubscriptionModal} className="btn-secondary hover-effect">
                  Maybe Later
                </Button>
              </div>

              <div className="modal-dont-show-again">
                <Checkbox
                  checked={dontShowSubscriptionModal}
                  onChange={(e) => setDontShowSubscriptionModal(e.target.checked)}
                >
                  Don't show this again
                </Checkbox>
              </div>
            </>
          )}
        />}
      {/* Session Expired Modal */}
      <Modal
        open={sessionExpiredVisible}
        title="Session Expired"
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="login" type="primary" onClick={handleSessionExpiredConfirm}>
            Login Again
          </Button>
        ]}
      >
        <Typography.Paragraph>
          Your session has expired because the device limit was changed by your administrator.
          Please log in again to continue.
        </Typography.Paragraph>
      </Modal>
      {pageAccess.isChatEnabled && chatbotEnabled && (
        <Suspense fallback={null}>
          <ChatbotAssistant />
        </Suspense>
      )}
    </Layout>
  );
};

export default MainLayout;